import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import apiClient from '../lib/api-client';

interface Sprint {
  id: string;
  name: string;
  goal?: string;
  status: string;
  start_date?: string;
  end_date?: string;
  duration_days: number;
  capacity_points?: number;
  project_id: string;
  project_name?: string;
  created_at: string;
}

interface Ticket {
  id: string;
  title: string;
  status: string;
  priority: string;
  story_points?: number;
  assignee_id?: string;
  ticket_number: string;
  project_id: string;
}

export function SprintsPage() {
  const [selectedProject, setSelectedProject] = useState<string>('');
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newSprint, setNewSprint] = useState({ name: '', goal: '', duration_days: 14, capacity_points: 0 });
  const [activeTab, setActiveTab] = useState<'board' | 'backlog' | 'history'>('board');
  const queryClient = useQueryClient();

  const { data: projects = [] } = useQuery({
    queryKey: ['projects'],
    queryFn: async () => {
      const res = await apiClient.get('/projects');
      return res.data;
    },
  });

  const { data: sprints = [], isLoading: sprintsLoading } = useQuery({
    queryKey: ['sprints', selectedProject],
    queryFn: async () => {
      if (selectedProject) {
        // Project-scoped sprints
        const res = await apiClient.get(`/projects/${selectedProject}/sprints`);
        return res.data.data || res.data; // Handle both wrapped and direct responses
      } else {
        // Global sprints for admins
        const res = await apiClient.get('/sprints');
        return res.data;
      }
    },
  });

  const { data: tickets = [] } = useQuery({
    queryKey: ['tickets', selectedProject],
    queryFn: async () => {
      if (!selectedProject) return [];
      const res = await apiClient.get(`/projects/${selectedProject}/tickets`);
      return res.data.data || res.data; // Handle both wrapped and direct responses
    },
    enabled: !!selectedProject,
  });

  const createMutation = useMutation({
    mutationFn: async (data: typeof newSprint & { project_id: string }) => {
      if (!data.project_id) throw new Error('Project is required');
      const res = await apiClient.post(`/projects/${data.project_id}/sprints`, {
        name: data.name,
        goal: data.goal,
        duration_days: data.duration_days,
        capacity_points: data.capacity_points,
      });
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sprints'] });
      setShowCreateForm(false);
      setNewSprint({ name: '', goal: '', duration_days: 14, capacity_points: 0 });
    },
  });

  const startMutation = useMutation({
    mutationFn: async (id: string) => {
      const res = await apiClient.post(`/sprints/${id}/start`);
      return res.data;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['sprints'] }),
  });

  const completeMutation = useMutation({
    mutationFn: async (id: string) => {
      const res = await apiClient.post(`/sprints/${id}/complete`);
      return res.data;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['sprints'] }),
  });

  const addTicketMutation = useMutation({
    mutationFn: async ({ sprintId, ticketId }: { sprintId: string; ticketId: string }) => {
      const res = await apiClient.post(`/sprints/${sprintId}/tickets`, { ticket_id: ticketId });
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sprints'] });
      queryClient.invalidateQueries({ queryKey: ['tickets'] });
    },
  });

  const activeSprints = sprints.filter((s: Sprint) => s.status === 'active');
  const planningSprints = sprints.filter((s: Sprint) => s.status === 'planning');
  const completedSprints = sprints.filter((s: Sprint) => s.status === 'complete');

  // Backlog: tickets not in any sprint (simplified - tickets with no sprint association)
  const backlogTickets = tickets.filter((t: Ticket) => t.status !== 'closed' && t.status !== 'resolved');

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'bg-green-100 text-green-800';
      case 'planning': return 'bg-blue-100 text-blue-800';
      case 'review': return 'bg-yellow-100 text-yellow-800';
      case 'complete': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'critical': return 'bg-red-100 text-red-800';
      case 'high': return 'bg-orange-100 text-orange-800';
      case 'medium': return 'bg-yellow-100 text-yellow-800';
      case 'low': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 py-6">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Sprint Management</h1>
          <p className="text-gray-500 text-sm mt-1">Manage sprints, backlog, and track velocity</p>
        </div>
        <div className="flex gap-3">
          <select
            value={selectedProject}
            onChange={(e) => setSelectedProject(e.target.value)}
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm"
          >
            <option value="">All Projects</option>
            {projects.map((p: any) => (
              <option key={p.id} value={p.id}>{p.name}</option>
            ))}
          </select>
          <button
            onClick={() => setShowCreateForm(!showCreateForm)}
            disabled={!selectedProject}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
          >
            + New Sprint
          </button>
        </div>
      </div>

      {!selectedProject && (
        <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <p className="text-blue-800 text-sm">
            💡 Select a project to manage sprints and view the backlog. Or view all sprints across projects in the global view.
          </p>
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-1 mb-6 border-b border-gray-200">
        {(['board', 'backlog', 'history'] as const).map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${
              activeTab === tab
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            {tab.charAt(0).toUpperCase() + tab.slice(1)}
          </button>
        ))}
      </div>

      {/* Create Sprint Form */}
      {showCreateForm && selectedProject && (
        <div className="bg-white border border-gray-200 rounded-lg p-6 mb-6">
          <h2 className="text-lg font-semibold mb-4">Create New Sprint</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Sprint Name</label>
              <input
                type="text"
                value={newSprint.name}
                onChange={(e) => setNewSprint({ ...newSprint, name: e.target.value })}
                placeholder="e.g. Sprint 1"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Duration (days)</label>
              <input
                type="number"
                value={newSprint.duration_days}
                onChange={(e) => setNewSprint({ ...newSprint, duration_days: parseInt(e.target.value) })}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Capacity (story points)</label>
              <input
                type="number"
                value={newSprint.capacity_points}
                onChange={(e) => setNewSprint({ ...newSprint, capacity_points: parseInt(e.target.value) })}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Project</label>
              <input
                type="text"
                value={projects.find((p: any) => p.id === selectedProject)?.name || 'Unknown'}
                disabled
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm bg-gray-50"
              />
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Sprint Goal</label>
              <textarea
                value={newSprint.goal}
                onChange={(e) => setNewSprint({ ...newSprint, goal: e.target.value })}
                placeholder="What do we want to achieve?"
                rows={2}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
              />
            </div>
          </div>
          <div className="flex gap-3 mt-4">
            <button
              onClick={() => createMutation.mutate({ ...newSprint, project_id: selectedProject })}
              disabled={!newSprint.name || !selectedProject || createMutation.isPending}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm disabled:opacity-50"
            >
              {createMutation.isPending ? 'Creating...' : 'Create Sprint'}
            </button>
            <button
              onClick={() => setShowCreateForm(false)}
              className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 text-sm"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Sprint Board */}
      {activeTab === 'board' && (
        <div>
          {sprintsLoading ? (
            <p className="text-gray-500">Loading sprints...</p>
          ) : (
            <>
              {/* Active Sprints */}
              {activeSprints.length > 0 ? (
                <div className="space-y-6">
                  {activeSprints.map((sprint: Sprint) => (
                    <div key={sprint.id} className="bg-white border border-green-200 rounded-lg p-6">
                      <div className="flex justify-between items-start mb-4">
                        <div>
                          <div className="flex items-center gap-3">
                            <h2 className="text-xl font-bold text-gray-900">{sprint.name}</h2>
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(sprint.status)}`}>
                              {sprint.status}
                            </span>
                            {sprint.project_name && (
                              <span className="px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
                                {sprint.project_name}
                              </span>
                            )}
                          </div>
                          {sprint.goal && <p className="text-gray-600 mt-1">{sprint.goal}</p>}
                        </div>
                        <button
                          onClick={() => completeMutation.mutate(sprint.id)}
                          disabled={completeMutation.isPending}
                          className="px-3 py-1.5 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm disabled:opacity-50"
                        >
                          Complete Sprint
                        </button>
                      </div>
                      <div className="grid grid-cols-4 gap-4 text-sm text-gray-600">
                        <div>
                          <span className="font-medium">Start:</span>{' '}
                          {sprint.start_date ? new Date(sprint.start_date).toLocaleDateString() : 'N/A'}
                        </div>
                        <div>
                          <span className="font-medium">End:</span>{' '}
                          {sprint.end_date ? new Date(sprint.end_date).toLocaleDateString() : 'N/A'}
                        </div>
                        <div>
                          <span className="font-medium">Duration:</span> {sprint.duration_days} days
                        </div>
                        <div>
                          <span className="font-medium">Capacity:</span> {sprint.capacity_points || 0} pts
                        </div>
                      </div>
                      {/* Progress bar placeholder */}
                      <div className="mt-4 bg-gray-200 rounded-full h-2">
                        <div className="bg-green-600 h-2 rounded-full" style={{ width: '45%' }} />
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12 bg-white rounded-lg border border-gray-200">
                  <p className="text-gray-500">
                    {selectedProject ? 'No active sprints in this project. Create one or start a planning sprint.' : 'No active sprints across all projects.'}
                  </p>
                </div>
              )}

              {/* Planning Sprints */}
              {planningSprints.length > 0 && (
                <div className="mt-8">
                  <h2 className="text-lg font-semibold text-gray-900 mb-4">Planning</h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {planningSprints.map((sprint: Sprint) => (
                      <div key={sprint.id} className="bg-white border border-blue-200 rounded-lg p-4">
                        <div className="flex justify-between items-start">
                          <h3 className="font-semibold text-gray-900">{sprint.name}</h3>
                          <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getStatusColor(sprint.status)}`}>
                            {sprint.status}
                          </span>
                        </div>
                        {sprint.project_name && (
                          <p className="text-xs text-gray-500 mt-1">{sprint.project_name}</p>
                        )}
                        {sprint.goal && <p className="text-gray-600 text-sm mt-1">{sprint.goal}</p>}
                        <div className="text-sm text-gray-500 mt-2">
                          {sprint.duration_days} days · {sprint.capacity_points || 0} pts
                        </div>
                        <button
                          onClick={() => startMutation.mutate(sprint.id)}
                          disabled={startMutation.isPending}
                          className="mt-3 w-full px-3 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm disabled:opacity-50"
                        >
                          Start Sprint
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* Backlog */}
      {activeTab === 'backlog' && (
        <div>
          {selectedProject ? (
            <>
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-lg font-semibold text-gray-900">
                  Backlog ({backlogTickets.length} tickets)
                </h2>
              </div>
              {backlogTickets.length > 0 ? (
                <div className="bg-white border border-gray-200 rounded-lg divide-y divide-gray-100">
                  {backlogTickets.map((ticket: Ticket) => (
                    <div key={ticket.id} className="p-4 flex justify-between items-center hover:bg-gray-50">
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <span className="text-xs font-mono text-gray-400">{ticket.ticket_number}</span>
                          <span className="font-medium text-gray-900">{ticket.title}</span>
                        </div>
                        <div className="flex gap-2 mt-1">
                          <span className={`px-2 py-0.5 rounded text-xs font-medium ${getPriorityColor(ticket.priority)}`}>
                            {ticket.priority}
                          </span>
                          <span className="text-xs text-gray-500">{ticket.story_points || 0} pts</span>
                        </div>
                      </div>
                      {activeSprints.length > 0 && (
                        <button
                          onClick={() => addTicketMutation.mutate({ sprintId: activeSprints[0].id, ticketId: ticket.id })}
                          disabled={addTicketMutation.isPending}
                          className="px-3 py-1 bg-blue-50 text-blue-700 rounded text-xs hover:bg-blue-100 disabled:opacity-50"
                        >
                          + Add to Sprint
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12 bg-white rounded-lg border border-gray-200">
                  <p className="text-gray-500">No tickets in backlog for this project</p>
                </div>
              )}
            </>
          ) : (
            <div className="text-center py-12 bg-white rounded-lg border border-gray-200">
              <p className="text-gray-500">Select a project to view its backlog</p>
            </div>
          )}
        </div>
      )}

      {/* History */}
      {activeTab === 'history' && (
        <div>
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Sprint History</h2>
          {completedSprints.length > 0 ? (
            <div className="space-y-3">
              {completedSprints.map((sprint: Sprint) => (
                <div key={sprint.id} className="bg-white border border-gray-200 rounded-lg p-4 flex justify-between items-center">
                  <div>
                    <div className="flex items-center gap-2">
                      <h3 className="font-semibold text-gray-900">{sprint.name}</h3>
                      {sprint.project_name && (
                        <span className="px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
                          {sprint.project_name}
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-gray-500">
                      {sprint.start_date ? new Date(sprint.start_date).toLocaleDateString() : '?'} →{' '}
                      {sprint.end_date ? new Date(sprint.end_date).toLocaleDateString() : '?'}
                    </p>
                  </div>
                  <div className="text-right">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(sprint.status)}`}>
                      {sprint.status}
                    </span>
                    <p className="text-sm text-gray-500 mt-1">{sprint.capacity_points || 0} pts capacity</p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12 bg-white rounded-lg border border-gray-200">
              <p className="text-gray-500">
                {selectedProject ? 'No completed sprints in this project yet' : 'No completed sprints across all projects yet'}
              </p>
            </div>
          )}
        </div>
      )}

      {/* Stats Summary */}
      <div className="mt-8 grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white border border-gray-200 rounded-lg p-4 text-center">
          <p className="text-2xl font-bold text-blue-600">{activeSprints.length}</p>
          <p className="text-sm text-gray-500">Active Sprints</p>
        </div>
        <div className="bg-white border border-gray-200 rounded-lg p-4 text-center">
          <p className="text-2xl font-bold text-green-600">{completedSprints.length}</p>
          <p className="text-sm text-gray-500">Completed</p>
        </div>
        <div className="bg-white border border-gray-200 rounded-lg p-4 text-center">
          <p className="text-2xl font-bold text-orange-600">{backlogTickets.length}</p>
          <p className="text-sm text-gray-500">Backlog Items</p>
        </div>
        <div className="bg-white border border-gray-200 rounded-lg p-4 text-center">
          <p className="text-2xl font-bold text-purple-600">{planningSprints.length}</p>
          <p className="text-sm text-gray-500">In Planning</p>
        </div>
      </div>
    </div>
  );
}

export default SprintsPage;